import { useMutation, useQueryClient } from "@tanstack/react-query"
import { ListTodo, Plus, X } from "lucide-react"
import { useState } from "react"

import { api, ErrorApi } from "@/api/cliente"

/**
 * El troceo de quien ejecuta: cinco casillas que se marcan.
 *
 * ⚠ **No son subtareas.** Una subtarea es trabajo con responsable, vencimiento y su
 * sitio en el tablero; esto es el cuaderno de quien está haciendo la tarea. Por eso
 * se crean, se corrigen y se borran sin pedirle permiso a nadie, y por eso **no
 * avisan**: un aviso por casilla marcada apaga la campanita en dos días.
 *
 * ⚠ **La barra de progreso se deriva, no se guarda.** Un porcentaje almacenado se
 * desincroniza de las casillas en cuanto alguien borra una, y entonces la ficha dice
 * 80 % con tres de cuatro hechas.
 */

export type Microtarea = {
  id: number
  titulo: string
  hecha: boolean
  orden: number
}

type Props = {
  tareaId: number
  microtareas: Microtarea[]
  puedeEditar: boolean
  onCambio: () => void
}

export function Microtareas({ tareaId, microtareas, puedeEditar, onCambio }: Props) {
  const [titulo, setTitulo] = useState("")
  const clienteQuery = useQueryClient()

  const refrescar = () => {
    clienteQuery.invalidateQueries({ queryKey: ["tarea", tareaId] })
    onCambio()
  }

  const crear = useMutation({
    mutationFn: (texto: string) =>
      api.post(`/api/v1/tareas/${tareaId}/microtareas`, { titulo: texto }),
    onSuccess: () => {
      setTitulo("")
      refrescar()
    },
  })

  const marcar = useMutation({
    mutationFn: ({ id, hecha }: { id: number; hecha: boolean }) =>
      api.patch(`/api/v1/tareas/${tareaId}/microtareas/${id}`, { hecha }),
    onSuccess: refrescar,
  })

  const borrar = useMutation({
    mutationFn: (id: number) => api.delete(`/api/v1/tareas/${tareaId}/microtareas/${id}`),
    onSuccess: refrescar,
  })

  const hechas = microtareas.filter((m) => m.hecha).length
  const total = microtareas.length
  const porcentaje = total === 0 ? 0 : Math.round((hechas / total) * 100)

  return (
    <section className="mt-5">
      <h3 className="flex items-center gap-2 text-sm font-medium">
        <ListTodo size={14} aria-hidden={true} />
        Microtareas
        {total > 0 && (
          <span className="text-xs tabular-nums" style={{ color: "var(--fg-muted)" }}>
            {hechas}/{total}
          </span>
        )}
      </h3>

      {total > 0 && (
        <div
          className="mt-2 h-1.5 w-full overflow-hidden rounded-full"
          role="progressbar"
          aria-valuenow={porcentaje}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Avance de las microtareas"
          style={{ backgroundColor: "var(--border)" }}
        >
          {/* Se anima el ancho de una barra, no una fila de tabla: aquí el
              movimiento ES la información —la barra creció— y no hay sondeo que
              lo haga temblar cada minuto. */}
          <div
            className="h-full transition-[width] duration-300"
            style={{ width: `${porcentaje}%`, backgroundColor: "var(--primary)" }}
          />
        </div>
      )}

      {total === 0 ? (
        <p className="mt-1 text-sm" style={{ color: "var(--fg-muted)" }}>
          Sin trocear.
        </p>
      ) : (
        <ul className="mt-2 space-y-1">
          {microtareas.map((m) => (
            <li key={m.id} className="flex items-center gap-2">
              <input
                type="checkbox"
                id={`micro-${m.id}`}
                checked={m.hecha}
                disabled={!puedeEditar || marcar.isPending}
                onChange={(e) => marcar.mutate({ id: m.id, hecha: e.target.checked })}
                className="h-4 w-4 shrink-0"
              />
              <label
                htmlFor={`micro-${m.id}`}
                className="min-w-0 flex-1 text-sm"
                style={{
                  textDecoration: m.hecha ? "line-through" : undefined,
                  color: m.hecha ? "var(--fg-muted)" : undefined,
                }}
              >
                {m.titulo}
              </label>
              {puedeEditar && (
                <button
                  type="button"
                  aria-label={`Quitar ${m.titulo}`}
                  onClick={() => borrar.mutate(m.id)}
                  style={{ color: "var(--fg-muted)" }}
                >
                  <X size={12} aria-hidden={true} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {puedeEditar && (
        <form
          className="mt-2 flex gap-2"
          onSubmit={(e) => {
            e.preventDefault()
            if (titulo.trim() !== "") crear.mutate(titulo.trim())
          }}
        >
          <input
            aria-label="Nueva microtarea"
            className="campo"
            placeholder="Trocear en un paso más…"
            value={titulo}
            onChange={(e) => setTitulo(e.target.value)}
          />
          <button
            type="submit"
            disabled={titulo.trim() === "" || crear.isPending}
            aria-label="Añadir microtarea"
            className="boton-secundario px-2 disabled:opacity-50"
          >
            <Plus size={14} aria-hidden={true} />
          </button>
        </form>
      )}

      {(crear.isError || marcar.isError || borrar.isError) && (
        <p role="alert" className="mt-2 text-sm" style={{ color: "var(--destructive)" }}>
          {crear.error instanceof ErrorApi ? crear.error.message : "No se pudo guardar el cambio."}
        </p>
      )}
    </section>
  )
}
